/**
 * SpaNavManager manages navigation of reports as a single page application.
 * i.e user feels like new page was loaded, but in reality report was swapped without a page refresh.
 * NOTE: This currently only works for iron apps.
 */

import {viewedReportFlagManager} from 'common/report/queries/app-api/user-flags';
import {trackVisitedReport} from 'common/util/server-tracking';
import {VisitedReportReason} from 'common/util/server-tracking';
import {updateHelpCenterFromUrl} from 'common/report/external-widgets/zendesk';
import {updateCommunityWidgetFromUrl} from 'common/report/external-widgets/insided';
import {clearAllToasts} from 'common/components/toast-area/toast-manager';
import {createElem} from 'common/util/dom';
import {apiClient} from 'common/util/api-client';

import {waitUntilUiReady} from '../perf-measure';
import {layoutManager} from './layout-manager';
import {IronAppInfo, urlToIronAppInfo} from './index';
import {parseStateFromUrl, UrlParsedState} from './state-from-url';

export type InitAppFunc = () => HTMLElement;

export interface AppAssets {
  js: string[];
  css: string[];
}

export interface AppAssetsUrls {
  [app: string]: AppAssets;
}

export interface InitOptions {
  projectId: string;
  projectName: string;
}

// api/app/asset-manifest/iron-apps is handled specially by webpack/dev-server-middleware.js for localhost
const locationOrigin = window.location.hostname === `localhost` ? window.location.origin : ``;
const appAssetsApiUrl = `${locationOrigin}/api/app/asset-manifest/iron-apps`;

export class SpaNavManager {
  private initAppFuncsMap: Map<string, InitAppFunc> = new Map();
  private appAssetsUrlMap: Map<string, AppAssets> = new Map();
  private initOptions: InitOptions;
  public initialized = false;

  /** iron apps call into this via init-app.ts when initializing */
  setInitAppFn(appName: string, initAppFunc: InitAppFunc) {
    this.initAppFuncsMap.set(appName, initAppFunc);
  }

  async initialize(initOptions: InitOptions) {
    if (this.initialized) {
      return;
    }

    this.initOptions = initOptions;
    const appAssetUrls = await this.fetchAppAssetUrls();
    this.setAppAssetsUrls(appAssetUrls);
    this.initialized = true;
  }

  setAppAssetsUrls(appsAssetUrls: AppAssetsUrls) {
    if (appsAssetUrls) {
      this.appAssetsUrlMap = new Map(Object.entries(appsAssetUrls));
    }
  }

  fetchAppAssetUrls() {
    return apiClient.get<AppAssetsUrls>(appAssetsApiUrl);
  }

  getIronAppInfo(urlState: UrlParsedState): IronAppInfo {
    let appInfo = null;
    const appNames = new Set(this.appAssetsUrlMap.keys());
    if (appNames.size) {
      const appName = urlState?.appName;
      appInfo = urlToIronAppInfo[appName];
      if (!appInfo || !appNames.has(appInfo.appName)) {
        appInfo = null;
      }
    }
    return appInfo;
  }

  isNavigable(nextUrlPath: string, curUrlPath): boolean {
    const curUrlState = parseStateFromUrl(curUrlPath);
    const nextUrlState = parseStateFromUrl(nextUrlPath);
    const curApp = this.getIronAppInfo(curUrlState);
    const nextApp = this.getIronAppInfo(nextUrlState);

    // If you try to go to the current app, the actual behavior is to refresh the page.
    // If `appName` variable is the same, we don't have to ask to user with a "confirm" dialog.
    return Boolean(
      curApp &&
        nextApp &&
        curApp.appName !== nextApp.appName &&
        curUrlState.projectId === nextUrlState.projectId &&
        curUrlState.workspaceId === nextUrlState.workspaceId,
    );
  }

  async navigate(newUrlPath: string, curUrlPath: string, ev: Event | null) {
    const startTime = Date.now();
    const curApp = this.getIronAppInfo(parseStateFromUrl(curUrlPath));
    const newApp = this.getIronAppInfo(parseStateFromUrl(newUrlPath));

    if (this.initOptions.projectId) {
      viewedReportFlagManager.increment(); // async, no need to wait
    }

    if (!curApp || !newApp || curApp.appName === newApp.appName) {
      // nothing to do, let the app's built-in router handle the navigation or let the browser refresh the page
      return;
    }

    if (ev && ev.type === `click`) {
      // don't reload the page with new url, we'll do a spa navigate
      ev.preventDefault();
    }

    const $mixpanelApp = document.querySelector(`#mixpanel-application`);

    // innerHTML = ``  will disconnect existing top level app component
    // and browser will recursively call disconnectedCallback for the component tree
    $mixpanelApp.innerHTML = ``;
    // e.g MusicFinder / Jql Console
    document.title = `${this.initOptions.projectName} / ${newApp.title}`;
    $mixpanelApp.appendChild(createElem(`mp-spinner`, {'allow-festive': true, size: `large`}));

    layoutManager.reset();

    // change the url without re-loading to the new app's url
    if (ev && ev.type !== `popstate`) {
      // if we push on a popstate event, we break browser back button navigation, don't do that
      window.history.pushState(``, ``, newUrlPath);
    }

    // disable old css and add new css if not already loaded
    // if new css is already there, just activate it
    const appAssetsUrlMap = this.appAssetsUrlMap;
    const $curAppCss: any = document.querySelector(`head > link[href='${appAssetsUrlMap.get(curApp.appName).css[0]}']`);
    const $newAppCss: any = document.querySelector(`head > link[href='${appAssetsUrlMap.get(newApp.appName).css[0]}']`);
    if ($curAppCss) {
      $curAppCss.disabled = true; // see https://guides.codechewing.com/js/disable-enable-stylesheet-javascript
    }
    if ($newAppCss) {
      $newAppCss.disabled = false;
    } else {
      document.head.appendChild(
        createElem(`link`, {rel: `stylesheet`, href: `${appAssetsUrlMap.get(newApp.appName).css[0]}`}),
      );
    }

    // add new js script if not already loaded
    // otherwise re-initialize existing app via it's initAppFunc
    if (!this.initAppFuncsMap.has(newApp.appName)) {
      document.body.appendChild(
        createElem(`script`, {type: `text/javascript`, src: `${appAssetsUrlMap.get(newApp.appName).js[0]}`}),
      );
    } else {
      const initAppFunc = this.initAppFuncsMap.get(newApp.appName);
      const appElem = initAppFunc();
      $mixpanelApp.innerHTML = ``;
      $mixpanelApp.appendChild(appElem);
    }

    // wait for report shell to load and track report loaded events
    await waitUntilUiReady();
    const elapsedMs = Date.now() - startTime;

    // update external widget suggestions
    updateHelpCenterFromUrl();
    updateCommunityWidgetFromUrl();

    clearAllToasts();

    this._trackReportLoaded(newApp.appName, elapsedMs);
  }

  /**
   * track 'Viewed Report' and 'Report Loaded' events with 'is_spa_nav' flag so we can pretend a pageload occured
   */
  private _trackReportLoaded(report: string, elapsedMs: number) {
    const projectId = this.initOptions.projectId;
    const commonTrackProps = {
      project_id: projectId,
      is_spa_nav: true,
      tab: report,
    };

    mixpanel.track(`Viewed report`, commonTrackProps);

    trackVisitedReport({
      projectId,
      reportName: report,
      reason: VisitedReportReason.ReportVoltNavigate,
    });

    mixpanel.track(`Report Loaded`, {
      ...commonTrackProps,
      page_time_to_ui_ready_ms: elapsedMs,
    });

    mixpanel.infra_metrics.track(`report-loaded`, {
      ...commonTrackProps,
      distinct_id: projectId,
      page_time_to_ui_ready_ms: elapsedMs,
    });
  }
}

// window singleton
export const spaNavManager: SpaNavManager = (window[`__spaNavManager`] =
  window[`__spaNavManager`] || new SpaNavManager());
